import { cached, tracked } from '@glimmer/tracking';
import StoreService from '@ember-data/store';
import { isPresent } from '@ember/utils';
import DataModel from '@ember-data/model';
import type ModelRegistry from 'ember-data/types/registries/model';

export enum FilterComponent {
  multiSelect = 'multi-select',
  multiSelectList = 'multi-select-list',
  singleSelect = 'single-select',
  month = 'month',
}

export enum FilterOptionsType {
  store = 'store',
  list = 'list',
}

export enum FilterId {
  clientName = 'client_name',
  property = 'property',
  airbnbQualityState = 'airbnb_quality_state',
  airbnbOfficialLink = 'airbnb_official_link',
  airbnbOfficialConnection = 'airbnb_official_connection',
  period = 'period',
  clientStatementStatus = 'client_statement_status',
  clientStatementPaymentStatus = 'client_statement_payment_status',
  upsellOrderStatus = 'upsell_order_status',
  airbnbCancellationPolicy = 'airbnb_cancellation_policy',
  country = 'country',
  city = 'city',
  knowledgeCenterCategory = 'knowledge_center_category',
  knowledgeCenterStatus = 'knowledge_center_status',
  protectionStatus = 'protection_status',
  disputeStatus = 'dispute_status',
  bookingWindow = 'booking_window',
}

/**
 * @description Filter params structure:
 * - params have value(can be string(for a single select filter), array of strings(for a multi select filter) and so on)
 * - params have `updateValue` method, the method correctly updates the `value` field
 */
export interface IBaseFilterParams<Value, UpdateValue> {
  value?: Value;

  filter: IBaseFilter<Value, UpdateValue>;
  selectedRecords?: unknown[];
  updateValue(value: UpdateValue): void;
  toQuery(): unknown;
  reset(): void;
}

/**
 * @description Filter structure:
 * - filter has component(all options are listed in the `FilterComponent` enum)
 * - filter has params(defined above)
 */
interface IBaseFilter<Value, UpdateValue> {
  component: FilterComponent;
  clearable: boolean;
  params: IBaseFilterParams<Value, UpdateValue>;
  updateValue(value: UpdateValue): void;
  toQuery(): unknown;
  reset(): void;
}

/**
 * @description Value - is the type of params value(can be string, array of strings, and so on)
 * UpdateValue - is the type of the `updateValue` argument(can be string, boolean, and so on)
 * Model - is the potential model name, for the filters that are taking their options list from the ember data store
 */
export abstract class BaseAbstractFilter<
  Value,
  UpdateValue,
> implements IBaseFilter<Value, UpdateValue> {
  store!: StoreService;

  abstract id: FilterId;
  abstract component: FilterComponent;
  abstract optionsType: FilterOptionsType;

  modelName?: keyof ModelRegistry;
  modelField?: keyof DataModel;
  optionsList?: UpdateValue[];
  clearable = true;

  constructor(params: IBaseFilterParams<Value, UpdateValue>) {
    this.params = params;
    this.params.filter = this;
  }

  @tracked params!: IBaseFilterParams<Value, UpdateValue>;

  @cached
  get models(): UpdateValue[] {
    return this.store.peekAll(this.modelName as keyof ModelRegistry).slice();
  }

  @cached
  get options(): UpdateValue[] {
    switch (this.optionsType) {
      case FilterOptionsType.list:
        return this.optionsList as UpdateValue[];
      case FilterOptionsType.store:
        return this.models;
      default:
        throw new Error(
          `unknown filter options type, expected to be 'FilterOptionsType', got ${this.optionsType}`,
        );
    }
  }

  public updateValue(value: UpdateValue): void {
    this.params.updateValue(value);
  }

  public toQuery<T>(): T {
    return this.params.toQuery() as T;
  }

  public reset(): void {
    this.params.reset();
  }
}

/**
 * @description Creating a base params class, so that other params classes would extend from the base one
 * defines that a instance of a params class, will have a parent reference to the filter
 */
export abstract class BaseAbstractFilterParams<Value> {
  filter;

  @tracked value?: Value;

  constructor(value?: Value) {
    if (isPresent(value)) {
      this.value = value as Value;
    }
  }
}
